/* ============================================================
   PreferencesContext — the user's cross-device settings (appearance, week-start,
   digest times, quiet hours), synced with the backend (/v1/preferences).

   This is the single source of truth for them. ThemeContext reads its appearance
   choice from here (and resolves it against the OS); the Review/heatmap views
   read week-start via useWeekStart().

   Offline-first, but much lighter than the store (see docs/offline-first.md):
   - Values are cached on disk per account and adopted before the first paint, so
     a cold launch with no signal shows the user's own settings rather than the
     app defaults.
   - A change is applied locally, written to disk, then PATCHed. Whatever hasn't
     been acknowledged is remembered per field, so it can be sent later.
   - The server is only re-read when nothing is pending. This is the part that
     makes the cache worth anything: a GET adopts the server's copy wholesale, so
     without a record of unsent fields an offline change would come back, look
     like it stuck, and then silently revert on the next successful sync.

   Why a per-field pending patch and not the store's outbox. Structurally it
   can't be the outbox: this provider sits above the store in the tree (the store
   reads `weekStartsOn` from it). But it also shouldn't be — a preference PATCH is
   idempotent and last-writer-wins per field, so there's nothing to order and
   nothing to replay exactly once. Toggle a switch five times offline and one
   value is sent, the last. Per FIELD rather than a whole-object push so a change
   made on another device we haven't seen yet isn't clobbered.

   And why there's no retry scheduler: a change is durable the moment it's on
   disk, and it gets another chance on the next change, foreground, or launch.
   Backoff timers would buy a few seconds of recovery latency on something nobody
   is watching, so the only failure handled explicitly is a server that refuses
   the patch outright — that one can't be retried into working.

   It sits BELOW AuthProvider (so useApi has a token, and so the cache can be
   namespaced by the signed-in user) and ABOVE the front end's ThemeProvider
   (which consumes the appearance choice). See app/App.tsx for the provider
   order.
   ============================================================ */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import { useApi } from '../api/useApi';
import type { QuietHours, ServerPreferences } from '../api/types';
import { useClientCore, usePersist } from '../provider';
import { dispositionFor } from '../store/outbox';
import type { PrefFields, PrefPatch } from '../store/persist';
import type { ThemePref } from '../hues';
import type { WeekStart } from '../dates';

interface PreferencesValue {
  /** appearance choice — what the Settings picker shows (ThemeContext resolves it) */
  themePref: ThemePref;
  setThemePref: (p: ThemePref) => void;
  /** the weekday a week begins on (getDay() index, 0 = Sunday) */
  weekStartsOn: WeekStart;
  setWeekStartsOn: (w: WeekStart) => void;
  /** master switch — whether the user wants the digests at all (persisted intent;
      each device still owns its OS notification permission + push registration). */
  remindersEnabled: boolean;
  setRemindersEnabled: (on: boolean) => void;
  /** consent to PROMOTIONAL push — product news, offers, win-back. Deliberately
      NOT implied by `remindersEnabled`: Guideline 4.5.4 wants marketing push to
      carry its own explicit opt-in and its own opt-out. Granted only by the
      first-run notifications screen (whose copy asks for it) and revoked from
      the matching Settings row. */
  productNewsEnabled: boolean;
  setProductNewsEnabled: (on: boolean) => void;
  /** morning summary time as minutes since local midnight; null = that digest off. */
  morningSummaryTime: number | null;
  setMorningSummaryTime: (minutes: number | null) => void;
  /** evening review time as minutes since local midnight; null = that digest off. */
  eveningReviewTime: number | null;
  setEveningReviewTime: (minutes: number | null) => void;
  /** IANA time zone the server schedules the digests against (detected on-device). */
  timezone: string;
  /** local quiet-hours window; null = off. May wrap midnight (start > end). */
  quietHours: QuietHours | null;
  setQuietHours: (window: QuietHours | null) => void;
}

/** The locally-held preference values, narrowed to the app's own types (the
   cached/wire shape in store/persist.ts is looser — `weekStartsOn` is a bare
   number there, and is validated on the way in). `timezone` isn't here: this
   device re-detects its own zone every launch and is the authority on it. */
interface Values {
  themePref: ThemePref;
  weekStartsOn: WeekStart;
  remindersEnabled: boolean;
  productNewsEnabled: boolean;
  morningSummaryTime: number | null;
  eveningReviewTime: number | null;
  quietHours: QuietHours | null;
}

/** First-run values. These match the backend column defaults, so the first paint
   of a brand-new account doesn't visibly correct itself once the server answers. */
const DEFAULTS: Values = {
  themePref: 'system',
  weekStartsOn: 1, // Monday
  remindersEnabled: false,
  productNewsEnabled: false, // silence is not consent — only the prompt screen grants it
  morningSummaryTime: 540, // 9:00 AM
  eveningReviewTime: 1260, // 9:00 PM
  quietHours: null,
};

/** This device's IANA time zone (e.g. "America/New_York"), or a safe fallback.
    The digests are server-generated, so the server needs each user's zone to fire
    at their local morning/evening — we detect it here and sync it up. */
function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && typeof tz === 'string' ? tz : 'UTC';
  } catch {
    return 'UTC';
  }
}

const THEME_PREFS = ['light', 'dark', 'system'] as const;

function asThemePref(v: unknown): ThemePref | null {
  return typeof v === 'string' && (THEME_PREFS as readonly string[]).includes(v)
    ? (v as ThemePref)
    : null;
}

/** A weekStartsOn is only trusted when it's a valid getDay() index. */
function asWeekStart(v: unknown): WeekStart | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 6 ? (v as WeekStart) : null;
}

/** Minutes since local midnight, or null for "off". `undefined` = absent or
   unusable, which callers treat as "leave the current value alone". */
function asMinutes(v: unknown): number | null | undefined {
  if (v === null) return null;
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 1439 ? v : undefined;
}

function asQuietHours(v: unknown): QuietHours | null | undefined {
  if (v === null) return null;
  if (!v || typeof v !== 'object') return undefined;
  const w = v as Partial<QuietHours>;
  const start = asMinutes(w.start);
  const end = asMinutes(w.end);
  // a window needs both ends; either may be 0 (midnight), so check the type
  return typeof start === 'number' && typeof end === 'number' ? { start, end } : undefined;
}

/** Merge a partial (from the server, from disk, or from a local change) onto
   known-good values. Every field is validated on the way in, so one bad value —
   a corrupt cache, an older or newer server — is ignored rather than adopted,
   and can't reach the theme or the week-start maths. */
function mergeValues(base: Values, p: Partial<PrefFields>): Values {
  const next: Values = { ...base };
  const themePref = asThemePref(p.themePref);
  if (themePref) next.themePref = themePref;
  const weekStartsOn = asWeekStart(p.weekStartsOn);
  if (weekStartsOn !== null) next.weekStartsOn = weekStartsOn;
  if (typeof p.remindersEnabled === 'boolean') next.remindersEnabled = p.remindersEnabled;
  if (typeof p.productNewsEnabled === 'boolean') next.productNewsEnabled = p.productNewsEnabled;
  const morning = asMinutes(p.morningSummaryTime);
  if (morning !== undefined) next.morningSummaryTime = morning;
  const evening = asMinutes(p.eveningReviewTime);
  if (evening !== undefined) next.eveningReviewTime = evening;
  const quiet = asQuietHours(p.quietHours);
  if (quiet !== undefined) next.quietHours = quiet;
  return next;
}

/** The fields `body` asked to change, taken from the values that were actually
   accepted. Queueing this rather than `body` itself keeps the server from being
   handed a value the local validators refused — if one was refused, the current
   value goes out instead, which is a no-op rather than a bad write. `timezone`
   isn't a local value, so it passes straight through. */
function acceptedPatch(next: Values, body: PrefPatch): PrefPatch {
  const out: PrefPatch = {};
  if ('themePref' in body) out.themePref = next.themePref;
  if ('weekStartsOn' in body) out.weekStartsOn = next.weekStartsOn;
  if ('remindersEnabled' in body) out.remindersEnabled = next.remindersEnabled;
  if ('productNewsEnabled' in body) out.productNewsEnabled = next.productNewsEnabled;
  if ('morningSummaryTime' in body) out.morningSummaryTime = next.morningSummaryTime;
  if ('eveningReviewTime' in body) out.eveningReviewTime = next.eveningReviewTime;
  if ('quietHours' in body) out.quietHours = next.quietHours;
  if (body.timezone !== undefined) out.timezone = body.timezone;
  return out;
}

const PreferencesContext = createContext<PreferencesValue | null>(null);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  // The cache is namespaced per account so two users on one device never adopt
  // each other's settings. Non-null only when signed in, which is also exactly
  // when there's a server to talk to.
  const userKey = status === 'signedIn' ? (user?.sub ?? null) : null;
  const api = useApi();
  const disk = usePersist();
  const { lifecycle } = useClientCore().adapters;
  // Stable handle so the effects don't re-run when a token refresh hands back a
  // new client (matches store/api.tsx).
  const apiRef = useRef(api);
  apiRef.current = api;

  const [values, setValues] = useState<Values>(DEFAULTS);
  // Freshest values, updated synchronously, so a change can be written to disk
  // and merged onto without waiting for a render.
  const valuesRef = useRef(values);
  // The device's zone — resolved once. We never adopt the server's stored zone
  // (this device is the authority on where it is); instead we push ours up so a
  // move across zones re-targets the digests. Not state: it doesn't drive
  // rendering and won't change mid-session.
  const timezoneRef = useRef(detectTimezone());

  /* ---------- the pending patch ---------- */
  // Field changes the server hasn't acknowledged, coalesced. Empty = in sync.
  const pendingRef = useRef<PrefPatch>({});
  // Disk writes stay off until the cache has been read, so startup (and a user
  // switch) can't overwrite one account's settings with another's.
  const armedRef = useRef(false);
  const flushPromiseRef = useRef<Promise<void> | null>(null);

  /** Write values + pending patch under one key. Called before the network is
     touched, so a change survives a kill even if the request never goes out. */
  const persist = useCallback(() => {
    if (!userKey || !armedRef.current) return;
    void disk.savePrefs(userKey, { values: valuesRef.current, pending: pendingRef.current });
  }, [userKey, disk]);

  /** Take values from the server or the cache. Doesn't touch the pending patch —
     an unsent change is the newer intent and must survive being adopted over. */
  const adopt = useCallback((p: Partial<PrefFields>) => {
    const next = mergeValues(valuesRef.current, p);
    valuesRef.current = next;
    setValues(next);
  }, []);

  /** Adopt the server's saved preferences. Only safe with nothing pending — it
     replaces local values wholesale. Never throws. */
  const refetch = useCallback(async () => {
    try {
      const p = await apiRef.current.get<ServerPreferences>('/v1/preferences');
      adopt(p);
      // Re-target the digests if this device has moved zones since the last sync
      // (or the server has never had one). Held like any other unsent change, so
      // it survives being offline rather than being a fire-and-forget write.
      if (p.timezone !== timezoneRef.current) {
        pendingRef.current = { ...pendingRef.current, timezone: timezoneRef.current };
      }
      persist();
    } catch {
      // Offline or refused — the cached (or default) values stand. A later
      // change, foreground return, or launch retries.
    }
  }, [adopt, persist]);

  /** Try to send the pending patch. Never throws, and never schedules a retry —
     on failure the fields stay pending on disk and go out on the next change,
     foreground, or launch. Concurrent callers join the attempt in progress. */
  const flush = useCallback((): Promise<void> => {
    if (flushPromiseRef.current) return flushPromiseRef.current;
    const run = async () => {
      let healNeeded = false;
      while (Object.keys(pendingRef.current).length > 0) {
        // Claim the current fields; anything the user changes mid-flight
        // accumulates in a fresh pending patch and goes out on the next pass.
        const sending = pendingRef.current;
        pendingRef.current = {};
        try {
          await apiRef.current.patch('/v1/preferences', sending);
          persist();
        } catch (err) {
          // Put the fields back — but never over a newer value set while this was
          // in flight; the user's latest choice always wins.
          pendingRef.current = { ...sending, ...pendingRef.current };
          if (dispositionFor(err) === 'drop') {
            // The server refuses this patch on its merits, so no number of
            // retries will land it. Discard it and adopt what the server does
            // have, so the UI stops showing a value that will never stick.
            pendingRef.current = {};
            healNeeded = true;
          }
          persist();
          break;
        }
      }
      if (healNeeded) await refetch();
    };
    const p = run().finally(() => {
      flushPromiseRef.current = null;
    });
    flushPromiseRef.current = p;
    return p;
  }, [persist, refetch]);

  /** Apply a preference change: local state, then disk, then the server. */
  const change = useCallback(
    (body: PrefPatch) => {
      const next = mergeValues(valuesRef.current, body);
      valuesRef.current = next;
      setValues(next);
      pendingRef.current = { ...pendingRef.current, ...acceptedPatch(next, body) };
      persist();
      void flush();
    },
    [persist, flush],
  );

  // Startup (and user switch): adopt the cache, flush what we owe the server,
  // then re-read it. Declared BEFORE the listener effect below so its synchronous
  // disarm runs first on a user change.
  useEffect(() => {
    // A user switch must not carry the previous account's values or unsent
    // changes into the new one. The old account's pending patch is already on its
    // own key and goes out when it signs back in; what's in memory has to go,
    // because the API client now carries a different token.
    armedRef.current = false;
    pendingRef.current = {};
    if (!userKey) return;
    let alive = true;
    (async () => {
      const cached = await disk.loadPrefs(userKey);
      if (!alive) return;
      if (cached) {
        adopt(cached.values);
        pendingRef.current = cached.pending;
      }
      armedRef.current = true;
      // Unsent changes go BEFORE the fetch: adopting the server's copy first
      // would overwrite a setting the user changed offline.
      await flush();
      if (!alive) return;
      if (Object.keys(pendingRef.current).length > 0) return; // still offline
      await refetch();
      if (!alive) return;
      // The fetch may have turned up a time-zone move to report.
      if (Object.keys(pendingRef.current).length > 0) await flush();
    })();
    return () => {
      alive = false;
    };
  }, [userKey, adopt, flush, refetch, disk]);

  // Returning to the foreground is the most likely moment for connectivity to
  // have come back, and it's the retry that replaces a backoff timer.
  useEffect(
    () =>
      lifecycle.subscribe((next) => {
        if (next === 'active') void flush();
      }),
    [lifecycle, flush],
  );

  const setThemePref = useCallback((p: ThemePref) => change({ themePref: p }), [change]);
  const setWeekStartsOn = useCallback((w: WeekStart) => change({ weekStartsOn: w }), [change]);
  const setRemindersEnabled = useCallback(
    (on: boolean) => change({ remindersEnabled: on }),
    [change],
  );
  const setProductNewsEnabled = useCallback(
    (on: boolean) => change({ productNewsEnabled: on }),
    [change],
  );
  const setMorningSummaryTime = useCallback(
    (minutes: number | null) => change({ morningSummaryTime: minutes }),
    [change],
  );
  const setEveningReviewTime = useCallback(
    (minutes: number | null) => change({ eveningReviewTime: minutes }),
    [change],
  );
  const setQuietHours = useCallback(
    (window: QuietHours | null) => change({ quietHours: window }),
    [change],
  );

  const value = useMemo<PreferencesValue>(
    () => ({
      themePref: values.themePref,
      setThemePref,
      weekStartsOn: values.weekStartsOn,
      setWeekStartsOn,
      remindersEnabled: values.remindersEnabled,
      setRemindersEnabled,
      productNewsEnabled: values.productNewsEnabled,
      setProductNewsEnabled,
      morningSummaryTime: values.morningSummaryTime,
      setMorningSummaryTime,
      eveningReviewTime: values.eveningReviewTime,
      setEveningReviewTime,
      timezone: timezoneRef.current,
      quietHours: values.quietHours,
      setQuietHours,
    }),
    [
      values,
      setThemePref,
      setWeekStartsOn,
      setRemindersEnabled,
      setProductNewsEnabled,
      setMorningSummaryTime,
      setEveningReviewTime,
      setQuietHours,
    ],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}

/** Week-start preference + setter, for the Settings picker and week views. */
export function useWeekStart(): { weekStartsOn: WeekStart; setWeekStartsOn: (w: WeekStart) => void } {
  const { weekStartsOn, setWeekStartsOn } = usePreferences();
  return { weekStartsOn, setWeekStartsOn };
}
